import React, { useRef, useState } from "react";
import api, { resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PhotoManager({ photos, onChange, testidPrefix = "photo" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      onChange([...photos, ...uploaded]);
      toast.success(`${uploaded.length} photo(s) uploaded`);
    } catch {
      toast.error("Upload failed. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;
    onChange([...photos, urlInput.trim()]);
    setUrlInput("");
  };

  const remove = (i) => onChange(photos.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-manager`}>
      <div className="flex flex-wrap gap-3">
        {photos.map((p, i) => (
          <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-border group">
            <img src={resolveImg(p)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`${testidPrefix}-remove-${i}`}
              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`${testidPrefix}-upload-tile`}
          className="w-24 h-24 rounded-lg border-2 border-dashed border-gold/50 hover:border-gold flex flex-col items-center justify-center text-muted-foreground hover:text-gold transition-colors"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5 mb-1" /><span className="text-[10px]">Upload</span></>}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid={`${testidPrefix}-file-input`} />
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="or paste an image URL" className="pl-9" data-testid={`${testidPrefix}-url-input`}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }} />
        </div>
        <Button type="button" variant="outline" onClick={addUrl} data-testid={`${testidPrefix}-add-url`}>Add</Button>
      </div>
    </div>
  );
}
